import { Colorways } from 'types/constants';
import { ReactNode } from 'react';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const getIdFromText = (text: string) => {
  if (!text) return '';

  text = text.toLowerCase().replace(/[^a-z0-9-]/g, '-');

  return encodeURIComponent(text);
};

export const getTextFromChildren = (children: ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return children.toString();
  }

  if (Array.isArray(children)) {
    return children.map(getTextFromChildren).join('');
  }

  if (typeof children === 'object' && children && 'props' in children) {
    return getTextFromChildren((children as { props: { children: ReactNode } }).props.children);
  }

  return '';
};

export const getCleanDocument = (document: Document | object): Document | object => {
  if (document && typeof document === 'object' && 'json' in document) {
    return document.json as Document;
  }

  return document;
};

export const extractTextFromNode = (node: unknown): string => {
  if (!node || typeof node !== 'object') {
    return '';
  }

  // If the node has a value property, it's a text node
  if ('value' in node && typeof node.value === 'string') {
    return node.value;
  }

  // If the node has content, recursively process all child nodes
  if ('content' in node && Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join('');
  }

  return '';
};

export const getRichTextValue = (document: Document | object): string => {
  if (document && typeof document === 'object' && 'json' in document) {
    return extractTextFromNode(document.json);
  }

  if (typeof document === 'string') {
    return document;
  }

  return '';
};

export const getColorwayName = (colorway: { name: Colorways } | Colorways | null | undefined): Colorways => {
  if (!colorway) {
    return Colorways.Primary;
  }

  if (typeof colorway === 'object' && 'name' in colorway) {
    return colorway.name;
  }

  return colorway;
};

export const pascalToSnake = (str: string) => {
  return str
    .replace(/([a-z])([A-Z])/g, '$1_$2') // Insert an underscore before uppercase letters
    .toLowerCase(); // Convert the whole string to lowercase
};

export const pascalToCamel = (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
};

export const titleToCamel = (str: string) => {
  const stripped = str.replace(/\s+/g, '');
  return stripped.charAt(0).toLowerCase() + stripped.slice(1);
};

export const toSnakeCase = (str: string) => {
  return str.replace(/\W+/g, ' ').trim().toLowerCase().replace(/\s/g, '_');
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatDate = (str: string) => {
  const opts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  };
  const parsedDate = Date.parse(str);
  return new Date(parsedDate).toLocaleDateString('en-US', opts);
};

/** Reads key from an item that may be CustomQueryStringParameterItems or REST-adapted shape. */
function getParamKey(item: Record<string, unknown>): string | undefined {
  const key = item.key ?? item.paramKey;
  return typeof key === 'string' ? key : undefined;
}

/** Reads value from an item that may be CustomQueryStringParameterItems or REST-adapted shape. */
function getParamValue(item: Record<string, unknown>): string | undefined {
  const value = item.value ?? item.paramValue;
  return value !== undefined && value !== null ? String(value) : undefined;
}

export const convertCustomQspsToObject = (
  collectionItems: CustomQueryStringParameterItems[],
): Record<string, string> => {
  if (!collectionItems || collectionItems.length === 0) {
    return {};
  }

  return collectionItems.reduce(
    (acc, item) => {
      const raw = item as unknown as Record<string, unknown>;
      const key = getParamKey(raw);
      const value = getParamValue(raw);
      if (key !== undefined && key !== '') {
        acc[key] = value ?? '';
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};

/**
 * Returns the letter label for a given index (A, B, ... Z, AA, AB, ...)
 */
export const getLetterLabel = (index: number): string => {
  let label = '';
  let i = index;
  do {
    label = String.fromCharCode(65 + (i % 26)) + label;
    i = Math.floor(i / 26) - 1;
  } while (i >= 0);
  return label;
};

export const slugify = (str: string) => {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Mn}/gu, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export const unslugify = (slug: string) => {
  let title = slug.replace(/-/g, ' ');
  title = title.replace(/\b[a-z]/g, function (match) {
    return match.toUpperCase();
  });

  return title;
};
